import React from 'react';
import { Control, FieldErrors, UseFormClearErrors } from 'react-hook-form';
import {
  AttentionIcon,
  Box,
  Controller,
  ControllerWrapper,
  costFormater,
  FieldWrapper,
  Hint,
  Inscription,
  Label,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { AccumulatedItem } from '@/modules/deductibles/SpendTracker/hooks/useDeductibles.ts';
import { Dependent } from '@/modules/employee/employee.types';
import theme from '@/styles/theme';

import { DeductibleFormField } from './hooks/useDeductibleForm';
import { DeductibleFormFields } from './hooks/useDeductibleForm';
import {
  getIndividualTierInfoMessage,
  getMemberKey,
  isEmptyValue,
  isZeroEmptyTransition,
} from './utils/deductibleModalUtils';
import { PriceControl } from './DeductibleForm';

interface IndividualDeductibleFieldsProps {
  members?: Array<
    Dependent | { dependentId: string; employeeId: string; fullName: string; relationshipType: string }
  >;
  individualAccumulatedValues: AccumulatedItem[];
  errors: FieldErrors<DeductibleFormFields>;
  control: Control<DeductibleFormFields>;
  clearErrors: UseFormClearErrors<DeductibleFormFields>;
  individualPreview: Record<string, { tier?: string; is_tier_changed?: boolean }>;
  setIndividualPreview: React.Dispatch<
    React.SetStateAction<Record<string, { tier?: string; is_tier_changed?: boolean }>>
  >;
  lastIndividualValuesRef: React.MutableRefObject<Record<string, string>>;
  individualValueDebouncedChange: (key: string, value: string) => void;
  updateFamilyDeductibleSum: () => void;
  maxIndividualAmount?: number | null;
}

export const IndividualDeductibleFields: React.FC<IndividualDeductibleFieldsProps> = ({
  members,
  individualAccumulatedValues,
  errors,
  control,
  clearErrors,
  individualPreview,
  setIndividualPreview,
  lastIndividualValuesRef,
  individualValueDebouncedChange,
  updateFamilyDeductibleSum,
  maxIndividualAmount,
}) => (
  <Box gap="xs">
    {individualAccumulatedValues.map((item) => {
      const key = getMemberKey(item);
      const member = members?.find((m) =>
        item.dependentId ? m.dependentId === item.dependentId : m.dependentId === item.employeeId
      );
      const preview = key != null ? individualPreview[key] : undefined;

      return (
        <FieldWrapper key={key}>
          <Label name={`${DeductibleFormField.memberDeductible}.${key}`} isNarrow>
            Total for {member?.fullName || ''}
          </Label>
          <ControllerWrapper>
            <Box direction="row" align="center" gap="s">
              <Controller
                name={`${DeductibleFormField.memberDeductible}.${key}`}
                control={control}
                render={({ field }) => {
                  const errorMsg = errors?.memberDeductible?.[_.toString(key)]?.message;
                  const fieldValue = _.toNumber(field.value);
                  const exceedsMax = maxIndividualAmount != null && fieldValue > maxIndividualAmount;
                  const maxErrorText = exceedsMax
                    ? `Max individual deductible is ${costFormater(maxIndividualAmount, true)} for your health plan`
                    : undefined;
                  return (
                    <PriceControl
                      {...field}
                      errorText={errorMsg || maxErrorText}
                      onChange={(value: string) => {
                        const prevValue = lastIndividualValuesRef.current[_.toString(key)];
                        const normalized = _.toNumber(value);
                        const lastNormalized = _.toNumber(prevValue);
                        const isZeroFromEmpty = isZeroEmptyTransition(prevValue, value);

                        field.onChange(value);

                        if (isEmptyValue(value)) {
                          setIndividualPreview((prev) => {
                            const copy = { ...prev };
                            if (key !== null && key !== undefined) {
                              delete copy[key];
                            }
                            return copy;
                          });
                        }
                        if (normalized !== lastNormalized || isZeroFromEmpty) {
                          if (key !== null && key !== undefined) {
                            lastIndividualValuesRef.current[key] = value;
                          }
                          clearErrors(`${DeductibleFormField.memberDeductible}.${key}`);
                          individualValueDebouncedChange(_.toString(key), value);
                          updateFamilyDeductibleSum();
                        }
                      }}
                    />
                  );
                }}
              />
              {preview?.is_tier_changed && (
                <Box style={{ position: 'absolute', right: 135, cursor: 'pointer' }}>
                  <Hint hintAlign="right" hintElement={<AttentionIcon color={theme.colors.info} />}>
                    <Inscription textAlign="center">{getIndividualTierInfoMessage(preview.tier)}</Inscription>
                  </Hint>
                </Box>
              )}
            </Box>
          </ControllerWrapper>
        </FieldWrapper>
      );
    })}
  </Box>
);
